/**
 * Simple function to generate shadow sizes based on current shadow properties
 * @param {Object} shadowProps - Current shadow properties from store
 * @returns {Object} Object containing all shadow size CSS values
 */
import { hexToHsl, toHex } from './colorUtils';

export const generateShadowSizes = (shadowProps) => {
    const {
        'shadow-color': color = 'hsl(0 0% 0%)',
        'shadow-opacity': baseOpacity = 0.1,
        'shadow-offset-x': offsetX = '0px',
        'shadow-offset-y': offsetY = '1px', 
        'shadow-blur-radius': blurRadius = '3px',
        'shadow-spread': spread = '0px'
    } = shadowProps;

    // Helper function to create color with specific opacity
    const getColorWithOpacity = (opacity) => {
        let hslColor = color.trim();
        
        // If already HSL format, clean it up and use it
        if (hslColor.startsWith('hsl')) {
            // Remove any existing alpha/opacity
            hslColor = hslColor.replace(/hsla?\(/, 'hsl(').replace(/\s*\/\s*[\d.]+\s*\)/, ')').replace(/,\s*[\d.]+\s*\)/, ')');
        }
        // If hex format, convert to HSL
        else if (hslColor.startsWith('#')) {
            hslColor = hexToHsl(hslColor);
        }
        // For any other valid CSS color format (rgb, oklch, named colors, etc.)
        else {
            try {
                // Use the browser's native color parsing and our toHex utility
                const hexValue = toHex(hslColor);
                if (hexValue && hexValue !== '#000000' || hslColor.toLowerCase() === 'black') {
                    hslColor = hexToHsl(hexValue);
                } else {
                    // If toHex returns null or black (and it's not actually black), keep original
                    // This preserves the original color format for valid colors
                    return `${hslColor}`;
                }
            } catch (e) {
                // If all conversion attempts fail, keep the original color
                // This allows CSS to handle it natively
                return `${hslColor}`;
            }
        }
        
        // Add opacity to HSL format
        const baseColor = hslColor.replace(/\s*\/\s*[\d.]+\s*\)/, ')');
        return baseColor.replace(')', ` / ${opacity})`);
    };

    // Helper function to create a shadow layer
    const createShadow = (x, y, blur, spreadVal, opacity) => {
        return `${x} ${y} ${blur} ${spreadVal} ${getColorWithOpacity(opacity)}`;
    };

    // Calculate opacity values based on baseOpacity
    const opacityHalf = baseOpacity / 2;
    const opacityBase = baseOpacity;
    const opacityDouble = baseOpacity * 2.5;

    // Parse numeric values for calculations
    const baseY = parseInt(offsetY);
    const baseBlur = parseInt(blurRadius);
    const baseSpread = parseInt(spread);

    return {
        'shadow-2xs': createShadow(offsetX, offsetY, blurRadius, spread, opacityHalf),
        'shadow-xs': createShadow(offsetX, offsetY, blurRadius, spread, opacityHalf),
        'shadow-sm': `${createShadow(offsetX, offsetY, blurRadius, spread, opacityBase)}, ${createShadow(offsetX, `${baseY - 1}px`, `${baseBlur - 1}px`, `${baseSpread - 1}px`, opacityBase)}`,
        'shadow': `${createShadow(offsetX, offsetY, blurRadius, spread, opacityBase)}, ${createShadow(offsetX, `${baseY - 1}px`, `${baseBlur - 1}px`, `${baseSpread - 1}px`, opacityBase)}`,
        'shadow-md': `${createShadow(offsetX, offsetY, blurRadius, spread, opacityBase)}, ${createShadow(offsetX, `${baseY}px`, `${baseBlur + 1}px`, `${baseSpread - 1}px`, opacityBase)}`,
        'shadow-lg': `${createShadow(offsetX, offsetY, blurRadius, spread, opacityBase)}, ${createShadow(offsetX, `${baseY + 2}px`, `${baseBlur + 3}px`, `${baseSpread - 1}px`, opacityBase)}`,
        'shadow-xl': `${createShadow(offsetX, offsetY, blurRadius, spread, opacityBase)}, ${createShadow(offsetX, `${baseY + 6}px`, `${baseBlur + 7}px`, `${baseSpread - 1}px`, opacityBase)}`,
        'shadow-2xl': createShadow(offsetX, offsetY, blurRadius, spread, opacityDouble)
    };
};


// WEBPACK FOOTER //
// ./src/components/pages/in-context/SidebarNavigation/utils/shadowUtils.js